import { useMemo } from 'react';
import type { PublicView, PlayerPublic, WhisperPublic, WhisperRingEntry, WhisperRune } from '@ringmaster/shared';
import { BroadcastFrame, DESIGN_WIDTH, DESIGN_HEIGHT } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { LowerThird } from './components/index.js';
import { ornaments } from './ornaments.js';
import { Glyph } from '../ui/Glyph.js';
import { useIsSpeaking } from '../discord/voice/voiceStore.js';
import { whisperLeaderboard } from '../hand/WhisperScreens.js';

/**
 * WhisperStage — the shared-device broadcast surface for The Whisper (the Seer's
 * séance). THE_WHISPER_SPEC.md §11:
 *
 *   • The CANDLE dead-center IS the clock — wax level = `candleWaxPct`, and the
 *     flame reddens as the wax drops (no numeric timer).
 *   • A RING of player bubbles orbits the candle: avatar + name, a pending pill
 *     (hidden at 0), a `…` typing indicator, a frozen/shield state, and the
 *     talking-glow reused from the Discord voice indicators.
 *   • The popped submission reads aloud CENTER-STAGE: a big word + the submitter's
 *     name in gold (the Jackbox composition) — over the mystical arcana skin.
 *   • Confirmed clue RUNES orbit the candle (fact tag + ✓/✗), reusing arcanaWisps.
 *   • On SCORE, a leaderboard replaces the ring.
 *
 * Driven entirely by the PUBLIC view (`publicView.whisper`) — no secrets reach a
 * Stage. Authored in the 1920×1080 BroadcastFrame design space, like the Blood
 * Moon stage, and wrapped in the SAME proscenium shell + arcana SceneStack.
 */
export function WhisperStage({
  publicView,
  caption,
  isSpeaking = false,
}: {
  publicView: PublicView;
  caption: string | null;
  isSpeaking?: boolean;
}) {
  const phase = publicView.phase;
  const manifest = scenesForGame('whisper');
  const w = publicView.whisper;

  const nameMap = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p.name])),
    [publicView.players],
  );

  // SCORE (per-round) and END (final) both show the leaderboard; every other
  // phase shows the candle + ring composition.
  const content =
    phase === 'SCORE' || phase === 'END' ? (
      <ScoreStage publicView={publicView} isFinal={phase === 'END'} />
    ) : (
      <CandleStage publicView={publicView} whisper={w} nameMap={nameMap} />
    );

  const ornamentsAndContent = (
    <ProsceniumFrame
      cornerSrc={ornaments.filigreeCorner}
      grainSrc={ornaments.grainTile}
      lowerThird={<LowerThird caption={caption} isSpeaking={isSpeaking} />}
    >
      {content}
    </ProsceniumFrame>
  );

  const framed = (
    <BroadcastFrame background="transparent">
      {manifest ? (
        <SceneStack manifest={manifest} phase={phase}>
          {ornamentsAndContent}
        </SceneStack>
      ) : (
        ornamentsAndContent
      )}
    </BroadcastFrame>
  );

  const fill: React.CSSProperties = { position: 'absolute', inset: 0, background: 'var(--ink-0, #060406)' };
  return <div style={fill}>{framed}</div>;
}

// ─── The candle + ring composition (all phases except SCORE) ──────────────────

const CENTER_X = DESIGN_WIDTH / 2;
const CENTER_Y = DESIGN_HEIGHT / 2 + 40;
const RING_RADIUS = 380;
const RUNE_RADIUS = 250;

function CandleStage({
  publicView,
  whisper,
  nameMap,
}: {
  publicView: PublicView;
  whisper?: WhisperPublic;
  nameMap: Record<string, string>;
}) {
  const wax = whisper?.candleWaxPct ?? 100;
  const ring = whisper?.ring ?? [];
  const runes = whisper?.runes ?? [];
  const centerCard = whisper?.centerCard ?? null;
  const playerById = useMemo(
    () => Object.fromEntries(publicView.players.map((p) => [p.id, p])),
    [publicView.players],
  );

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 4 }}>
      <WhisperStageStyles />

      {/* Ring of player bubbles around the candle. */}
      {ring.map((entry, i) => {
        const player = playerById[entry.playerId];
        const angle = (i / Math.max(1, ring.length)) * Math.PI * 2 - Math.PI / 2;
        const x = CENTER_X + Math.cos(angle) * RING_RADIUS;
        const y = CENTER_Y + Math.sin(angle) * RING_RADIUS;
        const highlight = !!centerCard && nameMap[entry.playerId] === centerCard.name;
        return (
          <RingBubble
            key={entry.playerId}
            entry={entry}
            player={player}
            name={nameMap[entry.playerId] ?? entry.playerId}
            x={x}
            y={y}
            highlight={highlight}
          />
        );
      })}

      {/* Orbiting clue runes (fact tag + ✓/✗). Older ones already dropped server-
          side (cap ~6); here we just place what we're given around the candle. */}
      {runes.map((rune, i) => {
        const angle = (i / Math.max(1, runes.length)) * Math.PI * 2 + Math.PI / 6;
        const x = CENTER_X + Math.cos(angle) * RUNE_RADIUS;
        const y = CENTER_Y + Math.sin(angle) * RUNE_RADIUS;
        return <Rune key={`${rune.tag}-${i}`} rune={rune} x={x} y={y} />;
      })}

      {/* The candle, dead-center — the clock. */}
      <Candle waxPct={wax} x={CENTER_X} y={CENTER_Y} />

      {/* The popped submission, read aloud center-stage (over the candle). */}
      {centerCard && <CenterCard text={centerCard.text} name={centerCard.name} />}
    </div>
  );
}

// ─── The candle (wax = clock, flame reddens as it drops) ──────────────────────

const CANDLE_H = 300;
const CANDLE_W = 92;

function Candle({ waxPct, x, y }: { waxPct: number; x: number; y: number }) {
  const pct = Math.max(0, Math.min(100, waxPct));
  // Flame reddens as the wax drops: violet (calm) → amber → red (urgent).
  const flame =
    pct > 50 ? '#c9b6ff' : pct > 25 ? '#f0a94b' : '#ff5a4f';
  const flameGlow =
    pct > 50 ? 'rgba(201,182,255,0.6)' : pct > 25 ? 'rgba(240,169,75,0.65)' : 'rgba(255,90,79,0.75)';
  // Remaining wax column height.
  const waxH = (CANDLE_H * pct) / 100;

  return (
    <div
      style={{
        position: 'absolute',
        left: x - CANDLE_W / 2,
        top: y - CANDLE_H,
        width: CANDLE_W,
        height: CANDLE_H + 60,
      }}
    >
      {/* Ambient glow pool at the base. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: CANDLE_H - waxH - 40,
          width: 320,
          height: 320,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${flameGlow} 0%, rgba(0,0,0,0) 62%)`,
          filter: 'blur(4px)',
          pointerEvents: 'none',
        }}
      />

      {/* The wax column (drains from the top as time runs out). */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          width: CANDLE_W,
          height: CANDLE_H,
          borderRadius: '10px 10px 4px 4px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(44,22,80,0.25) 100%)',
          border: '1px solid rgba(201,182,255,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Filled wax (from bottom up to waxH). */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: waxH,
            background: 'linear-gradient(180deg, #efe7d0 0%, #d9c9a3 100%)',
            transition: 'height 900ms cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: 'inset 0 2px 6px rgba(255,255,255,0.5)',
          }}
        />
      </div>

      {/* Wick + flame at the top of the remaining wax. */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: CANDLE_H - waxH + 44,
          transform: 'translateX(-50%)',
          transition: 'top 900ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div
          className="whisper-flame"
          style={{
            width: 26,
            height: 46,
            margin: '0 auto',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            background: `radial-gradient(circle at 50% 70%, #fff 0%, ${flame} 55%, rgba(0,0,0,0) 100%)`,
            boxShadow: `0 0 40px 10px ${flameGlow}`,
          }}
        />
      </div>
    </div>
  );
}

// ─── Center read-aloud card (big word + submitter name in gold) ───────────────

function CenterCard({ text, name }: { text: string; name: string }) {
  return (
    <div className="whisper-center-card">
      <div className="whisper-center-word">{text}</div>
      <div className="whisper-center-name">{name}</div>
    </div>
  );
}

// ─── A ring bubble (avatar, name, pending pill, typing, frozen, talking glow) ──

function RingBubble({
  entry,
  player,
  name,
  x,
  y,
  highlight,
}: {
  entry: WhisperRingEntry;
  player: PlayerPublic | undefined;
  name: string;
  x: number;
  y: number;
  highlight: boolean;
}) {
  // Talking glow reuses the Discord voice indicator (keyed by discordUserId).
  const talking = useIsSpeaking(player?.discordUserId);
  const initial = name.charAt(0).toUpperCase();
  const frozen = entry.frozen;

  return (
    <div
      className={`whisper-bubble${highlight ? ' is-highlight' : ''}${talking ? ' is-talking' : ''}${frozen ? ' is-frozen' : ''}`}
      style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%, -50%)' }}
    >
      <div className="whisper-bubble-avatar">
        {player?.avatarUrl ? (
          <img src={player.avatarUrl} alt="" aria-hidden />
        ) : (
          <span>{initial}</span>
        )}
        {/* Frozen / shield overlay. */}
        {frozen && (
          <span className="whisper-bubble-shield" aria-hidden>
            <Glyph name="candle" size={28} tone="arcana" />
          </span>
        )}
        {/* Pending pill — hidden at 0. */}
        {entry.pending > 0 && <span className="whisper-bubble-pending">{entry.pending}</span>}
      </div>
      <div className="whisper-bubble-name">
        {name}
        {entry.typing && <span className="whisper-bubble-typing">…</span>}
      </div>
    </div>
  );
}

// ─── A clue rune (fact tag + ✓/✗) ─────────────────────────────────────────────

function Rune({ rune, x, y }: { rune: WhisperRune; x: number; y: number }) {
  return (
    <div
      className={`whisper-rune${rune.yes ? ' is-yes' : ' is-no'}`}
      style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%, -50%)' }}
    >
      <span className="whisper-rune-tag">{formatTag(rune.tag)}</span>
      <span className="whisper-rune-mark">{rune.yes ? '✓' : '✗'}</span>
    </div>
  );
}

/** Turn a camelCase fact-tag key into a short human label ('foundAtNight' → 'night'). */
function formatTag(tag: string): string {
  const map: Record<string, string> = {
    alive: 'alive',
    biggerThanPerson: 'big',
    foundAtNight: 'night',
    manMade: 'man-made',
    hasWings: 'wings',
    edible: 'edible',
    indoors: 'indoors',
    makesSound: 'sound',
    foundInNature: 'nature',
    hasColor: 'color',
  };
  return map[tag] ?? tag;
}

// ─── SCORE — the leaderboard ──────────────────────────────────────────────────

function ScoreStage({
  publicView,
  isFinal = false,
}: {
  publicView: PublicView;
  isFinal?: boolean;
}) {
  const w = publicView.whisper;
  // Room cumulative totals, not the engine-scoped whisper.scores — the standings
  // must agree with the end screens across a game switch.
  const rows = whisperLeaderboard(publicView);
  const winner = rows[0];

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
      <WhisperStageStyles />
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <Glyph name="candle" size={44} tone="arcana" />
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 64, margin: 0, color: 'var(--arcana-4, #c9b6ff)', letterSpacing: '0.03em' }}>
          {isFinal ? 'Final standings' : 'Standings'}
        </h1>
      </div>
      {isFinal && winner ? (
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 30, color: 'var(--gold-5, #e6b866)', margin: '0 0 28px', letterSpacing: '0.03em' }}>
          {winner.name} wins the séance
        </p>
      ) : w ? (
        <p style={{ fontFamily: 'var(--font-ui)', fontSize: 24, color: 'var(--text-secondary)', margin: '0 0 28px' }}>
          Round {w.roundIndex + 1} of {w.totalRounds}
        </p>
      ) : null}
      <ol className="whisper-stage-board">
        {rows.map((r, i) => (
          <li key={r.id} className={`whisper-stage-board-row${i === 0 ? ' is-leader' : ''}`}>
            <span className="whisper-stage-rank">{i + 1}</span>
            <span className="whisper-stage-board-name">{r.name}</span>
            <span className="whisper-stage-board-pts">{r.score}</span>
          </li>
        ))}
        {rows.length === 0 && (
          <li style={{ color: 'var(--text-tertiary)', fontSize: 24, textAlign: 'center', padding: 40, fontFamily: 'var(--font-ui)' }}>
            No scores yet.
          </li>
        )}
      </ol>
    </div>
  );
}

// ─── Scoped styles ────────────────────────────────────────────────────────────

function WhisperStageStyles() {
  return (
    <style>{`
      @keyframes whisper-flame-flicker {
        0%, 100% { transform: scaleY(1) scaleX(1) translateY(0); opacity: 0.95; }
        50% { transform: scaleY(1.08) scaleX(0.94) translateY(-2px); opacity: 1; }
      }
      .whisper-flame { animation: whisper-flame-flicker 1.4s ease-in-out infinite; transform-origin: 50% 100%; }

      .whisper-bubble {
        width: 150px; display: flex; flex-direction: column; align-items: center; gap: 10px;
        transition: transform 300ms var(--ease-standard);
      }
      .whisper-bubble.is-highlight { transform: translate(-50%, -50%) scale(1.12); }
      .whisper-bubble-avatar {
        position: relative; width: 108px; height: 108px; border-radius: 50%;
        background: linear-gradient(180deg, #2c1650 0%, #160b26 100%);
        border: 3px solid rgba(139,92,246,0.4);
        display: flex; align-items: center; justify-content: center;
        font-family: var(--font-display); font-size: 44px; color: var(--arcana-4, #c9b6ff);
        box-shadow: 0 8px 28px rgba(0,0,0,0.6);
        overflow: visible;
      }
      .whisper-bubble-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
      .whisper-bubble.is-highlight .whisper-bubble-avatar {
        border-color: var(--gold-5, #e6b866);
        box-shadow: 0 0 44px rgba(230,184,102,0.7);
      }
      .whisper-bubble.is-talking .whisper-bubble-avatar {
        border-color: #6ee787;
        box-shadow: 0 0 40px rgba(110,231,135,0.7);
      }
      .whisper-bubble.is-frozen .whisper-bubble-avatar {
        border-color: rgba(201,182,255,0.9);
        box-shadow: 0 0 40px rgba(139,92,246,0.8);
        filter: saturate(0.6) brightness(0.85);
      }
      .whisper-bubble-shield {
        position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
        background: rgba(22,11,38,0.55); border-radius: 50%;
      }
      .whisper-bubble-pending {
        position: absolute; top: -6px; right: -6px; min-width: 34px; height: 34px;
        padding: 0 8px; border-radius: 17px;
        background: var(--arcana-3, #8b5cf6); color: #fff;
        font-family: var(--font-ui); font-weight: 700; font-size: 20px;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      }
      .whisper-bubble-name {
        font-family: var(--font-ui); font-size: 24px; color: var(--text-primary);
        text-shadow: 0 2px 10px rgba(0,0,0,0.9); text-align: center;
        max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .whisper-bubble-typing { color: var(--arcana-4, #c9b6ff); margin-left: 4px; font-weight: 700; }

      .whisper-rune {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 8px 16px; border-radius: 999px;
        font-family: var(--font-ui); font-size: 22px;
        background: rgba(22,11,38,0.72); backdrop-filter: blur(4px);
        border: 1px solid rgba(139,92,246,0.4);
        box-shadow: 0 4px 16px rgba(0,0,0,0.5);
      }
      .whisper-rune-tag { color: var(--text-secondary); }
      .whisper-rune-mark { font-weight: 800; font-size: 24px; }
      .whisper-rune.is-yes .whisper-rune-mark { color: #6ee787; }
      .whisper-rune.is-no .whisper-rune-mark { color: #ff7a6f; }

      .whisper-center-card {
        position: absolute; left: 50%; top: 40%;
        transform: translate(-50%, -50%);
        display: flex; flex-direction: column; align-items: center; gap: 12px;
        padding: 40px 72px; border-radius: 28px;
        background: radial-gradient(120% 120% at 50% 0%, rgba(44,22,80,0.92) 0%, rgba(8,5,17,0.94) 100%);
        border: 1px solid rgba(139,92,246,0.5);
        box-shadow: 0 24px 80px rgba(0,0,0,0.7), 0 0 60px rgba(139,92,246,0.4);
        z-index: 10; max-width: 900px; text-align: center;
        animation: whisper-center-pop 420ms var(--ease-dramatic, cubic-bezier(0.2,0.8,0.2,1));
      }
      @keyframes whisper-center-pop {
        from { opacity: 0; transform: translate(-50%, -46%) scale(0.9); }
        to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      }
      .whisper-center-word {
        font-family: var(--font-display); font-size: 88px; line-height: 1.05;
        color: #fff; text-shadow: 0 0 32px rgba(201,182,255,0.8);
        word-break: break-word;
      }
      .whisper-center-name {
        font-family: var(--font-display); font-size: 40px;
        color: var(--gold-5, #e6b866); letter-spacing: 0.04em;
      }

      .whisper-stage-board {
        list-style: none; margin: 0; padding: 0; width: 100%; max-width: 760px;
        display: flex; flex-direction: column; gap: 14px;
      }
      .whisper-stage-board-row {
        display: flex; align-items: center; gap: 28px;
        padding: 20px 32px; border-radius: 18px;
        background: rgba(255,255,255,0.05); border: 1px solid rgba(139,92,246,0.3);
      }
      .whisper-stage-board-row.is-leader {
        background: linear-gradient(90deg, rgba(230,184,102,0.18) 0%, rgba(139,92,246,0.14) 100%);
        border-color: rgba(230,184,102,0.6);
      }
      .whisper-stage-rank { width: 48px; text-align: center; font-family: var(--font-display); font-size: 40px; color: var(--arcana-4, #c9b6ff); }
      .whisper-stage-board-name { flex: 1; font-family: var(--font-ui); font-size: 34px; color: var(--text-primary); }
      .whisper-stage-board-pts { font-family: var(--font-ui); font-weight: 800; font-size: 40px; color: #fff; font-variant-numeric: tabular-nums; }

      @media (prefers-reduced-motion: reduce) {
        .whisper-flame, .whisper-center-card { animation: none; }
      }
    `}</style>
  );
}
